import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../api/request/Lead/Lead.service";
import numberFormat from "../../../../../utils/Utils";

interface RetentionFlowProps {
  currentCapital: number;
  /** null = sin prima (cohorte SCOR FR): no se enseña la línea del precio. */
  currentPremium: number | null;
  beneficiaryNames: string[];
  onClose: () => void;
}

type Step = 1 | 2 | "success";
type Reason = "expensive" | "better" | "noNeed" | "other" | null;

const RetentionFlow = ({
  currentCapital,
  currentPremium,
  beneficiaryNames,
  onClose,
}: RetentionFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>(1);
  const [reason, setReason] = useState<Reason>(null);
  const [submitting, setSubmitting] = useState(false);

  const goToStep = (s: Step) => setStep(s);

  // The reason step no longer cancels the policy: it records a cancellation
  // request and notifies the advisors team by email so an agent follows up.
  const submitCancellationRequest = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      const leadRepository = new HttpLeadRepository(leadId, token);
      await leadRepository.postCancellationRequest(reason);
      goToStep("success");
    } catch (error) {
      setNotification({
        message: translate("newDashboard.retention.error"),
        type: "error",
        time: 5,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflow: "auto",
    padding: "32px 28px",
    position: "relative",
  };

  const lossItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "14px 16px",
    backgroundColor: "#fffbeb",
    borderRadius: 10,
    border: "1px solid #fef3c7",
  };

  const radioStyle = (selected: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 16px",
    borderRadius: 10,
    border: `2px solid ${selected ? "#4f46e5" : "#e5e7eb"}`,
    backgroundColor: selected ? "#eef2ff" : "#fff",
    cursor: "pointer",
    transition: "all 0.15s",
  });

  const textLinkStyle: React.CSSProperties = {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "#9ca3af",
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
    textAlign: "center",
    width: "100%",
    marginTop: 8,
  };

  const beneficiaryStr = beneficiaryNames.length > 0
    ? beneficiaryNames.join(" y ")
    : "tus beneficiarios";

  // ── STEP 1: LOSS AVERSION ─────────────────────────────────────────────
  const Step1 = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: "#fef3c7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <GlobalIcon iconName="AlertCircleIcon" size="S" color="#d97706" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", margin: 0, lineHeight: 1.4 }}>
          {translate("newDashboard.retention.step1.title")}
        </h2>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
        <div style={lossItemStyle}>
          <GlobalIcon iconName="ShieldCheckIcon" size="XS" color="#d97706" />
          <span style={{ fontSize: 15, color: "#92400e", flex: 1 }}>
            {translate("newDashboard.retention.step1.loseCapital").replace(
              "{capital}",
              numberFormat(currentCapital, "capital")
            )}
          </span>
        </div>
        <div style={lossItemStyle}>
          <GlobalIcon iconName="SimpleUserIcon" size="XS" color="#d97706" />
          <span style={{ fontSize: 15, color: "#92400e", flex: 1 }}>
            {translate("newDashboard.retention.step1.loseBeneficiaries").replace(
              "{names}",
              beneficiaryStr
            )}
          </span>
        </div>
        {null !== currentPremium && (
          <div style={lossItemStyle} data-testid="retention-lose-price">
            <GlobalIcon iconName="CurrencyEuroIcon" size="XS" color="#d97706" />
            <span style={{ fontSize: 15, color: "#92400e", flex: 1 }}>
              {translate("newDashboard.retention.step1.losePrice").replace(
                "{amount}",
                numberFormat(currentPremium, "price")
              )}
            </span>
          </div>
        )}
      </div>

      <ButtonDS
        label={translate("newDashboard.retention.step1.keepButton")}
        buttonType="primary"
        size="40"
        onClick={onClose}
        className="w-full"
      />
      <button
        onClick={() => goToStep(2)}
        style={textLinkStyle}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#6b7280"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
      >
        {translate("newDashboard.retention.step1.continueLink")}
      </button>
    </div>
  );

  // ── STEP 2: EXIT SURVEY ───────────────────────────────────────────────
  const reasons: { key: Reason; labelKey: string }[] = [
    { key: "expensive", labelKey: "newDashboard.retention.step2.reason.expensive" },
    { key: "better", labelKey: "newDashboard.retention.step2.reason.better" },
    { key: "noNeed", labelKey: "newDashboard.retention.step2.reason.noNeed" },
    { key: "other", labelKey: "newDashboard.retention.step2.reason.other" },
  ];

  const Step2 = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", margin: "0 0 20px", lineHeight: 1.4 }}>
        {translate("newDashboard.retention.step2.title")}
      </h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
        {reasons.map((r) => (
          <button
            key={r.key}
            onClick={() => setReason(r.key)}
            style={radioStyle(reason === r.key)}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: `2px solid ${reason === r.key ? "#4f46e5" : "#d1d5db"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {reason === r.key && (
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#4f46e5",
                  }}
                />
              )}
            </div>
            <span style={{ fontSize: 15, color: "#1e293b", textAlign: "left" }}>
              {translate(r.labelKey)}
            </span>
          </button>
        ))}
      </div>

      <ButtonDS
        label={translate("newDashboard.retention.step2.next")}
        buttonType="primary"
        size="40"
        onClick={submitCancellationRequest}
        disabled={!reason || submitting}
        className="w-full"
      />
      <button
        onClick={() => goToStep(1)}
        style={textLinkStyle}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#6b7280"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
      >
        {translate("newDashboard.retention.step2.backLink")}
      </button>
    </div>
  );

  // ── SUCCESS: REQUEST RECEIVED ─────────────────────────────────────────
  const SuccessStep = () => (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <GlobalIcon iconName="CircleCheckFilledIcon" size="S" color="#6b7280" />
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", margin: "0 0 12px" }}>
        {translate("newDashboard.retention.success.title")}
      </h2>
      <p style={{ fontSize: 15, color: "#64748b", lineHeight: 1.6, margin: "0 0 28px" }}>
        {translate("newDashboard.retention.success.desc")}
      </p>
      <ButtonDS
        label={translate("newDashboard.retention.success.closeButton")}
        buttonType="secondary"
        size="40"
        onClick={onClose}
        className="w-full"
      />
    </div>
  );

  const stepContent: Record<Step, JSX.Element> = {
    1: <Step1 />,
    2: <Step2 />,
    success: <SuccessStep />,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            border: "none",
            background: "none",
            cursor: "pointer",
            padding: 6,
            borderRadius: 6,
            display: "flex",
          }}
        >
          <GlobalIcon iconName="XIcon" size="XS" color="#9ca3af" />
        </button>

        {stepContent[step]}
      </div>
    </div>
  );
};

export default RetentionFlow;
